import { useEffect, useRef, useState } from "react";
import {
  Button,
  DatePicker,
  Input,
  Modal,
  Segmented,
  Select,
  Spin,
  Tag,
  Tooltip,
} from "antd";
import {
  CheckCircle,
  ChevronLeft,
  ClipboardList,
  MessageCircle,
  Mic,
  MicOff,
  PlusCircle,
  Share2,
  Users,
  UserPlus,
} from "lucide-react";
import dayjs from "dayjs";
import { apiRequest, useAccount } from "@vojomo-core/shared";
import { useSearchParams } from "react-router-dom";
import { OperatingLayout } from "layouts/operating/OperatingLayout";
import { AARVoPanel } from "./AARVoPanel";
import { AARGroupSession } from "./AARGroupSession";

// ─── Types ───────────────────────────────────────────────────────────────────

interface AARSession {
  id: string;
  meeting_name: string;
  meeting_date: string;
  status: "open" | "closed";
  mode: "solo" | "group";
  created_by: string;
  created_by_name: string;
  created_at: string;
  summary_shared: boolean;
  shared_with_users: string[];
}

interface AARResponse {
  id: string;
  user_id: string;
  user_name: string;
  q1_accomplish: string;
  q2_happened: string;
  q3_why: string;
  q4_start: string;
  submitted_at: string;
}

interface AARComment {
  id: string;
  user_id: string;
  user_name: string;
  section: string;
  content: string;
  created_at: string;
}

const QUESTIONS: {
  key: keyof Omit<AARResponse, "id" | "user_id" | "user_name" | "submitted_at">;
  label: string;
  voFocus: string;
  placeholder: string;
}[] = [
  {
    key: "q1_accomplish",
    label: "What was expected to happen?",
    voFocus: "Vo defines the objectives, plans, and intended results",
    placeholder: "Describe the goals, plans, and expected outcomes going into this…",
  },
  {
    key: "q2_happened",
    label: "What actually occurred?",
    voFocus: "Vo focuses on facts and key events",
    placeholder: "Stick to facts and key events — what actually happened, both good and bad…",
  },
  {
    key: "q3_why",
    label: "What went well and why? What should we commit to continuing?",
    voFocus: "Vo identifies successes to sustain",
    placeholder: "What specific actions drove positive outcomes? What should we never stop doing…",
  },
  {
    key: "q4_start",
    label: "What can be improved and how?",
    voFocus: "Vo identifies actionable lessons — this becomes part of your team's knowledge",
    placeholder: "What should change next time? Be specific about actions, processes, or behaviours…",
  },
];

const SECTION_OPTIONS = [
  { label: "General", value: "general" },
  { label: "Q1 — Expected", value: "q1_accomplish" },
  { label: "Q2 — Occurred", value: "q2_happened" },
  { label: "Q3 — Went well", value: "q3_why" },
  { label: "Q4 — Improvements", value: "q4_start" },
];

const BRAND = "rgb(44, 21, 97)";

// ─── Per-question mic hook ────────────────────────────────────────────────────

function usePerQuestionMic(
  onTranscribed: (key: string, text: string) => void
) {
  const [recordingKey, setRecordingKey] = useState<string | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const start = async (key: string, sessionId: string) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : "audio/mp4";
      const recorder = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mimeType });
        const ext = mimeType.includes("mp4") ? "mp4" : "webm";
        const file = new File([blob], `recording.${ext}`, { type: mimeType });
        const fd = new FormData();
        fd.append("file", file);
        const transcript = await apiRequest<string>({
          subRoute: "audio",
          path: `/transcribe_file?ref_type=aar&ref_id=${sessionId}`,
          method: "POST",
          formData: fd,
        });
        if (transcript) onTranscribed(key, transcript);
        setRecordingKey(null);
      };
      recorder.start();
      recorderRef.current = recorder;
      setRecordingKey(key);
    } catch {
      setRecordingKey(null);
    }
  };

  const stop = () => {
    recorderRef.current?.stop();
    recorderRef.current = null;
  };

  return { recordingKey, start, stop };
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function SessionCard({
  session,
  responseCount,
  onClick,
}: {
  session: AARSession;
  responseCount: number;
  onClick: () => void;
}) {
  const date = dayjs(session.meeting_date).format("MMM D, YYYY");
  const isGroup = session.mode === "group";
  return (
    <button
      onClick={onClick}
      className="w-full text-left border rounded-lg px-4 py-3 hover:border-purple-400 hover:bg-purple-50 transition-colors bg-white"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-semibold text-sm text-gray-900 truncate">
            {session.meeting_name}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">{date}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Tag color={isGroup ? "purple" : "blue"} className="text-xs">
            {isGroup ? "Group" : "Solo"}
          </Tag>
          <Tag
            color={session.status === "open" ? "processing" : "default"}
            className="text-xs"
          >
            {session.status === "open" ? "Open" : "Closed"}
          </Tag>
          {session.summary_shared && (
            <Tag color="green" className="text-xs">
              Shared
            </Tag>
          )}
          {!isGroup && (
            <Tooltip
              title={`${responseCount} response${responseCount !== 1 ? "s" : ""}`}
            >
              <span className="flex items-center gap-1 text-xs text-gray-500">
                <Users size={13} />
                {responseCount}
              </span>
            </Tooltip>
          )}
        </div>
      </div>
    </button>
  );
}

function ResponseCard({ resp }: { resp: AARResponse }) {
  return (
    <div className="border rounded-lg p-4 bg-white space-y-3">
      <div className="flex items-center justify-between">
        <span className="font-semibold text-sm text-purple-800">
          {resp.user_name || "Anonymous"}
        </span>
        <span className="text-xs text-gray-400">
          {dayjs(resp.submitted_at).format("MMM D, h:mm A")}
        </span>
      </div>
      {QUESTIONS.map((q) => {
        const answer = resp[q.key];
        if (!answer) return null;
        return (
          <div key={q.key}>
            <p className="text-xs font-medium text-gray-500 mb-0.5">
              {q.label}
            </p>
            <p className="text-sm text-gray-800 whitespace-pre-wrap">
              {answer}
            </p>
          </div>
        );
      })}
    </div>
  );
}

function CommentItem({ comment, canDelete, onDelete }: { comment: AARComment; canDelete: boolean; onDelete: () => void }) {
  const sectionLabel = SECTION_OPTIONS.find((o) => o.value === comment.section)?.label ?? comment.section;
  return (
    <div className="flex gap-3 py-3 border-b last:border-b-0">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-medium text-sm text-gray-800">{comment.user_name || "Anonymous"}</span>
          {comment.section !== "general" && (
            <Tag className="text-xs m-0" color="purple">{sectionLabel}</Tag>
          )}
          <span className="text-xs text-gray-400 ml-auto">{dayjs(comment.created_at).format("MMM D, h:mm A")}</span>
        </div>
        <p className="text-sm text-gray-700 m-0 whitespace-pre-wrap">{comment.content}</p>
      </div>
      {canDelete && (
        <button onClick={onDelete} className="text-gray-300 hover:text-red-400 text-xs shrink-0 mt-1">✕</button>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export const AAR = () => {
  const [searchParams] = useSearchParams();
  const { vojomoAccount } = useAccount();
  const userId = vojomoAccount?.["_id"] as string | undefined;
  const isCompanyAdmin = Boolean((vojomoAccount as unknown as Record<string, unknown>)?.["company_admin_companies"]?.toString());

  // List state
  const [sessions, setSessions] = useState<AARSession[]>([]);
  const [responseCounts, setResponseCounts] = useState<Record<string, number>>({});
  const [loadingSessions, setLoadingSessions] = useState(true);

  // Detail state
  const [activeSession, setActiveSession] = useState<AARSession | null>(null);
  const [responses, setResponses] = useState<AARResponse[]>([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [myResponse, setMyResponse] = useState<Partial<AARResponse>>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Sharing + comments
  const [sharing, setSharing] = useState(false);
  const [comments, setComments] = useState<AARComment[]>([]);
  const [newComment, setNewComment] = useState({ section: "general", content: "" });
  const [submittingComment, setSubmittingComment] = useState(false);

  // Grant access modal
  const [grantOpen, setGrantOpen] = useState(false);
  const [grantUserId, setGrantUserId] = useState("");
  const [grantUserName, setGrantUserName] = useState("");
  const [granting, setGranting] = useState(false);

  // Vo panel
  const [showVoPanel, setShowVoPanel] = useState(false);

  // Per-question mic
  const { recordingKey, start: startMic, stop: stopMic } = usePerQuestionMic(
    (key, text) => {
      setMyResponse((prev) => {
        const existing = (prev as Record<string, string>)[key] ?? "";
        return { ...prev, [key]: existing ? `${existing} ${text}` : text };
      });
    }
  );

  // Create modal
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDate, setNewDate] = useState<dayjs.Dayjs | null>(dayjs());
  const [newMode, setNewMode] = useState<"solo" | "group">("solo");
  const [creating, setCreating] = useState(false);

  // ── Fetch session list ──────────────────────────────────────────────────
  const fetchSessions = async () => {
    setLoadingSessions(true);
    const data = await apiRequest<{ sessions: AARSession[] }>({
      subRoute: "aar",
      path: "/sessions",
      method: "GET",
    });
    setSessions(data?.sessions ?? []);
    setLoadingSessions(false);
  };

  useEffect(() => {
    fetchSessions();
  }, []);

  // Auto-open a specific session when navigated from a roadmap session with ?session=<id>
  useEffect(() => {
    const targetId = searchParams.get("session");
    if (!targetId || sessions.length === 0) return;
    const match = sessions.find((s) => s.id === targetId);
    if (match && !activeSession) {
      openSession(match);
    }
  }, [sessions, searchParams]);

  // ── Open a session ─────────────────────────────────────────────────────
  const openSession = async (session: AARSession) => {
    setActiveSession(session);
    setShowVoPanel(false);
    setComments([]);

    if (session.mode === "group") {
      return;
    }

    setLoadingDetail(true);
    setResponses([]);
    setMyResponse({});
    setSaved(false);

    const data = await apiRequest<{
      session: AARSession;
      responses: AARResponse[];
      comments: AARComment[];
    }>({
      subRoute: "aar",
      path: `/sessions/${session.id}`,
      method: "GET",
    });

    const allResponses = data?.responses ?? [];
    setResponses(allResponses);
    setComments(data?.comments ?? []);

    const mine = allResponses.find((r) => r.user_id === userId);
    if (mine) setMyResponse(mine);

    setResponseCounts((prev) => ({
      ...prev,
      [session.id]: allResponses.length,
    }));
    setLoadingDetail(false);
  };

  // ── Submit / update response ───────────────────────────────────────────
  const submitResponse = async () => {
    if (!activeSession) return;
    setSaving(true);
    await apiRequest({
      subRoute: "aar",
      path: `/sessions/${activeSession.id}/respond`,
      method: "POST",
      body: myResponse,
    });
    setSaving(false);
    setSaved(true);
    await openSession(activeSession);
  };

  // ── Share summary ──────────────────────────────────────────────────────
  const shareSummary = async () => {
    if (!activeSession) return;
    setSharing(true);
    const data = await apiRequest<{ shared_with: number; session: AARSession }>({
      subRoute: "aar",
      path: `/sessions/${activeSession.id}/share`,
      method: "POST",
    });
    setSharing(false);
    if (data?.session) setActiveSession(data.session);
  };

  // ── Add comment ────────────────────────────────────────────────────────
  const addComment = async () => {
    if (!activeSession || !newComment.content.trim()) return;
    setSubmittingComment(true);
    const data = await apiRequest<{ comment: AARComment }>({
      subRoute: "aar",
      path: `/sessions/${activeSession.id}/comments`,
      method: "POST",
      body: newComment,
    });
    setSubmittingComment(false);
    if (data?.comment) {
      setComments((prev) => [...prev, data.comment]);
      setNewComment({ section: "general", content: "" });
    }
  };

  // ── Delete comment ─────────────────────────────────────────────────────
  const deleteComment = async (commentId: string) => {
    if (!activeSession) return;
    await apiRequest({
      subRoute: "aar",
      path: `/sessions/${activeSession.id}/comments/${commentId}`,
      method: "DELETE",
    });
    setComments((prev) => prev.filter((c) => c.id !== commentId));
  };

  // ── Grant access ───────────────────────────────────────────────────────
  const grantAccess = async () => {
    if (!activeSession || !grantUserId.trim()) return;
    setGranting(true);
    await apiRequest({
      subRoute: "aar",
      path: `/sessions/${activeSession.id}/grant-access`,
      method: "POST",
      body: { user_id: grantUserId.trim(), user_name: grantUserName.trim() },
    });
    setGranting(false);
    setGrantOpen(false);
    setGrantUserId("");
    setGrantUserName("");
    // Refresh session to pick up updated shared_with_users
    await openSession(activeSession);
  };

  // ── Create new session ─────────────────────────────────────────────────
  const createSession = async () => {
    if (!newName.trim() || !newDate) return;
    setCreating(true);
    const data = await apiRequest<{ session: AARSession }>({
      subRoute: "aar",
      path: "/sessions",
      method: "POST",
      body: {
        meeting_name: newName.trim(),
        meeting_date: newDate.format("YYYY-MM-DD"),
        mode: newMode,
      },
    });
    setCreating(false);
    setCreateOpen(false);
    setNewName("");
    setNewDate(dayjs());
    setNewMode("solo");
    await fetchSessions();
    if (data?.session) openSession(data.session);
  };

  // ── Helpers ─────────────────────────────────────────────────────────────
  const isCreator = activeSession?.created_by === userId;
  const isParticipant = responses.some((r) => r.user_id === userId);
  const isGranted = activeSession?.shared_with_users?.includes(userId ?? "");
  const canComment = isParticipant || isGranted || isCreator;
  const canShareSummary = isCreator && responses.length > 0;

  // ── Render: group detail view ──────────────────────────────────────────
  if (activeSession?.mode === "group") {
    return (
      <OperatingLayout pageHeader="">
        <div className="max-w-3xl mx-auto space-y-5">
          <div className="flex items-center gap-3">
            <Button
              icon={<ChevronLeft size={16} />}
              onClick={() => {
                setActiveSession(null);
                fetchSessions();
              }}
              size="small"
            >
              Back
            </Button>
            <div className="min-w-0">
              <h3 className="font-bold text-base text-gray-900 truncate m-0">
                {activeSession.meeting_name}
              </h3>
              <p className="text-xs text-gray-500 m-0">
                {dayjs(activeSession.meeting_date).format("MMMM D, YYYY")} ·
                Group AAR
              </p>
            </div>
            <Tag
              color={activeSession.status === "open" ? "processing" : "default"}
              className="ml-auto"
            >
              {activeSession.status === "open" ? "Open" : "Closed"}
            </Tag>
          </div>
          <AARGroupSession
            sessionId={activeSession.id}
            onSummaryShared={(updatedSession) => setActiveSession(updatedSession as unknown as AARSession)}
          />
        </div>
      </OperatingLayout>
    );
  }

  // ── Render: solo detail view ───────────────────────────────────────────
  if (activeSession) {
    const hasMyResponse = responses.some((r) => r.user_id === userId);
    const otherResponses = responses.filter((r) => r.user_id !== userId);

    return (
      <OperatingLayout pageHeader="">
        <div className="max-w-3xl mx-auto space-y-5">
          {/* Header */}
          <div className="flex items-center gap-3 flex-wrap">
            <Button
              icon={<ChevronLeft size={16} />}
              onClick={() => {
                setActiveSession(null);
                setShowVoPanel(false);
                fetchSessions();
              }}
              size="small"
            >
              Back
            </Button>
            <div className="min-w-0">
              <h3 className="font-bold text-base text-gray-900 truncate m-0">
                {activeSession.meeting_name}
              </h3>
              <p className="text-xs text-gray-500 m-0">
                {dayjs(activeSession.meeting_date).format("MMMM D, YYYY")} ·{" "}
                {responses.length} response{responses.length !== 1 ? "s" : ""}
              </p>
            </div>
            <div className="ml-auto flex items-center gap-2 flex-wrap">
              {canShareSummary && !activeSession.summary_shared && (
                <Button
                  icon={<Share2 size={15} />}
                  onClick={shareSummary}
                  loading={sharing}
                  size="small"
                  style={{ borderColor: BRAND, color: BRAND }}
                >
                  Share Summary
                </Button>
              )}
              {isCompanyAdmin && (
                <Tooltip title="Grant access to non-participants">
                  <Button
                    icon={<UserPlus size={15} />}
                    onClick={() => setGrantOpen(true)}
                    size="small"
                  />
                </Tooltip>
              )}
              <Button
                icon={<MessageCircle size={15} />}
                onClick={() => setShowVoPanel((v) => !v)}
                size="small"
                type={showVoPanel ? "primary" : "default"}
                style={showVoPanel ? { backgroundColor: BRAND } : undefined}
              >
                Chat with Vo
              </Button>
              <Tag
                color={activeSession.status === "open" ? "processing" : "default"}
              >
                {activeSession.status === "open" ? "Open" : "Closed"}
              </Tag>
              {activeSession.summary_shared && (
                <Tag color="green">Summary Shared</Tag>
              )}
            </div>
          </div>

          {loadingDetail ? (
            <div className="flex justify-center py-10">
              <Spin />
            </div>
          ) : (
            <>
              {/* My response form */}
              <div className="border rounded-lg p-5 bg-white space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-sm text-purple-900 m-0">
                    {hasMyResponse ? "Your Response" : "Submit Your Response"}
                  </h4>
                  {saved && (
                    <span className="flex items-center gap-1 text-xs text-green-600">
                      <CheckCircle size={13} /> Saved
                    </span>
                  )}
                </div>
                <div className="space-y-4">
                  {QUESTIONS.map((q) => {
                    const isRecording = recordingKey === q.key;
                    return (
                      <div key={q.key}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex-1 min-w-0">
                            <label className="text-xs font-medium text-gray-700">
                              {q.label}
                            </label>
                            <p className="text-[11px] text-purple-500 m-0 mt-0.5">
                              {q.voFocus}
                            </p>
                          </div>
                          <Tooltip
                            title={isRecording ? "Stop recording" : "Record answer with Vo"}
                          >
                            <button
                              type="button"
                              onClick={() =>
                                isRecording
                                  ? stopMic()
                                  : startMic(q.key, activeSession.id)
                              }
                              disabled={recordingKey !== null && !isRecording}
                              className={`flex items-center gap-1 px-2 py-1 rounded-full transition-colors ml-3 shrink-0 ${
                                isRecording
                                  ? "bg-red-100 text-red-500 animate-pulse"
                                  : "bg-purple-50 text-purple-500 hover:bg-purple-100"
                              } disabled:opacity-30`}
                            >
                              {isRecording ? (
                                <MicOff size={12} />
                              ) : (
                                <Mic size={12} />
                              )}
                              <span className="text-[10px] font-semibold">Vo</span>
                            </button>
                          </Tooltip>
                        </div>
                        <Input.TextArea
                          value={
                            (myResponse as Record<string, string>)[q.key] ?? ""
                          }
                          onChange={(e) =>
                            setMyResponse((prev) => ({
                              ...prev,
                              [q.key]: e.target.value,
                            }))
                          }
                          placeholder={q.placeholder}
                          autoSize={{ minRows: 2, maxRows: 6 }}
                          disabled={activeSession.status === "closed"}
                        />
                      </div>
                    );
                  })}
                </div>
                <Button
                  type="primary"
                  onClick={submitResponse}
                  loading={saving}
                  disabled={activeSession.status === "closed"}
                  style={{ backgroundColor: BRAND }}
                >
                  {hasMyResponse ? "Update Response" : "Submit Response"}
                </Button>
              </div>

              {/* Other responses */}
              {otherResponses.length > 0 && (
                <div className="space-y-3">
                  <h4 className="font-semibold text-sm text-gray-700 m-0">
                    Team Responses ({otherResponses.length})
                  </h4>
                  {otherResponses.map((r) => (
                    <ResponseCard key={r.id} resp={r} />
                  ))}
                </div>
              )}

              {responses.length === 0 && (
                <div className="text-center py-8 text-gray-400 text-sm">
                  No responses yet. Be the first to submit yours.
                </div>
              )}

              {/* Comments section — visible once summary has been shared */}
              {(activeSession.summary_shared || comments.length > 0) && (
                <div className="border rounded-lg p-5 bg-white space-y-4">
                  <h4 className="font-semibold text-sm text-gray-800 m-0 flex items-center gap-2">
                    <MessageCircle size={15} className="text-purple-500" />
                    Comments {comments.length > 0 && `(${comments.length})`}
                  </h4>
                  {comments.length > 0 ? (
                    <div className="divide-y">
                      {comments.map((c) => (
                        <CommentItem
                          key={c.id}
                          comment={c}
                          canDelete={c.user_id === userId || isCreator}
                          onDelete={() => deleteComment(c.id)}
                        />
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 m-0">
                      No comments yet. Be the first to add one.
                    </p>
                  )}
                  {canComment && (
                    <div className="space-y-2 pt-2 border-t">
                      <div className="flex gap-2">
                        <Select
                          value={newComment.section}
                          onChange={(v) => setNewComment((p) => ({ ...p, section: v }))}
                          options={SECTION_OPTIONS}
                          size="small"
                          className="w-40 shrink-0"
                        />
                      </div>
                      <Input.TextArea
                        value={newComment.content}
                        onChange={(e) => setNewComment((p) => ({ ...p, content: e.target.value }))}
                        placeholder="Add a comment…"
                        autoSize={{ minRows: 2, maxRows: 4 }}
                      />
                      <Button
                        size="small"
                        type="primary"
                        onClick={addComment}
                        loading={submittingComment}
                        disabled={!newComment.content.trim()}
                        style={{ backgroundColor: BRAND }}
                      >
                        Post Comment
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Vo slide-out panel */}
        {showVoPanel && (
          <AARVoPanel
            sessionId={activeSession.id}
            onExtract={(fields) => {
              setMyResponse((prev) => ({
                ...prev,
                ...Object.fromEntries(
                  Object.entries(fields).filter(([, v]) => Boolean(v))
                ),
              }));
            }}
            onClose={() => setShowVoPanel(false)}
          />
        )}

        {/* Grant access modal */}
        <Modal
          title="Grant Access to Non-Participant"
          open={grantOpen}
          onCancel={() => setGrantOpen(false)}
          footer={null}
          destroyOnClose
        >
          <div className="space-y-4 pt-2">
            <p className="text-sm text-gray-500 m-0">
              As a Company Admin, you can grant read + comment access to users who were not part of this AAR.
            </p>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">User ID</label>
              <Input
                placeholder="Paste the user's ID"
                value={grantUserId}
                onChange={(e) => setGrantUserId(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Display name (optional)</label>
              <Input
                placeholder="e.g. Jane Smith"
                value={grantUserName}
                onChange={(e) => setGrantUserName(e.target.value)}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button onClick={() => setGrantOpen(false)}>Cancel</Button>
              <Button
                type="primary"
                loading={granting}
                onClick={grantAccess}
                disabled={!grantUserId.trim()}
                style={{ backgroundColor: BRAND }}
              >
                Grant Access
              </Button>
            </div>
          </div>
        </Modal>
      </OperatingLayout>
    );
  }

  // ── Render: list view ──────────────────────────────────────────────────
  return (
    <OperatingLayout pageHeader="After Action Review">
      <div className="max-w-2xl space-y-5">
        {/* Page header row */}
        <div className="flex items-center justify-between">
          <h2 className="font-bold text-lg text-gray-900 m-0">After Action Review</h2>
          <Button
            type="primary"
            icon={<PlusCircle size={15} />}
            onClick={() => setCreateOpen(true)}
            style={{ backgroundColor: BRAND }}
          >
            New AAR
          </Button>
        </div>

        {/* Intro / explainer */}
        <div className="bg-purple-50 border border-purple-100 rounded-xl p-5 space-y-4">
          <div>
            <p className="text-sm font-semibold text-purple-900 m-0 mb-1">What is an After Action Review?</p>
            <p className="text-sm text-gray-600 m-0">
              An AAR is a structured debrief used by the world's highest-performing teams — originally developed
              by the U.S. Army — to turn every experience into a learning advantage. It answers four simple but
              powerful questions after any significant event, sprint, or quarter.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-2">
            {QUESTIONS.map((q, i) => (
              <div key={q.key} className="flex gap-2 items-start">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-purple-200 text-purple-800 text-[11px] font-bold flex items-center justify-center">
                  {i + 1}
                </span>
                <p className="text-xs text-gray-700 m-0 leading-relaxed">{q.label}</p>
              </div>
            ))}
          </div>
          <div className="border-t border-purple-100 pt-3 flex gap-3 flex-wrap text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <Mic size={12} className="text-purple-400" />
              <strong>Voice-first</strong> — Vo listens and extracts your answers
            </span>
            <span className="flex items-center gap-1">
              <MessageCircle size={12} className="text-purple-400" />
              <strong>Team insight</strong> — see every participant's perspective
            </span>
            <span className="flex items-center gap-1">
              <Share2 size={12} className="text-purple-400" />
              <strong>Lessons persist</strong> — improvements feed Vo's team memory
            </span>
          </div>
        </div>

        {loadingSessions ? (
          <div className="flex justify-center py-10">
            <Spin />
          </div>
        ) : sessions.length === 0 ? (
          <div className="border rounded-lg py-12 flex flex-col items-center gap-3 text-gray-400 bg-white">
            <ClipboardList size={36} strokeWidth={1.2} />
            <p className="text-sm m-0">
              No AARs yet. Create your first one to start building your team's learning library.
            </p>
            <Button
              type="primary"
              size="small"
              onClick={() => setCreateOpen(true)}
              style={{ backgroundColor: BRAND }}
            >
              Create AAR
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {sessions.map((s) => (
              <SessionCard
                key={s.id}
                session={s}
                responseCount={responseCounts[s.id] ?? 0}
                onClick={() => openSession(s)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Create modal */}
      <Modal
        title="New After Action Review"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        footer={null}
        destroyOnClose
      >
        <div className="space-y-4 pt-2">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Meeting / Review name
            </label>
            <Input
              placeholder="e.g. Q1 QBR Debrief, April Sprint Retro…"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onPressEnter={createSession}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Meeting date
            </label>
            <DatePicker
              value={newDate}
              onChange={setNewDate}
              className="w-full"
              format="MMMM D, YYYY"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-2">
              Mode
            </label>
            <Segmented
              value={newMode}
              onChange={(v) => setNewMode(v as "solo" | "group")}
              options={[
                { label: "Solo", value: "solo" },
                { label: "Group", value: "group" },
              ]}
              className="w-full"
            />
            <p className="text-xs text-gray-400 mt-1.5">
              {newMode === "solo"
                ? "Each team member submits their own written response."
                : "One shared recording — Vo synthesises the team discussion into a summary."}
            </p>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button
              type="primary"
              loading={creating}
              onClick={createSession}
              disabled={!newName.trim() || !newDate}
              style={{ backgroundColor: BRAND }}
            >
              Create
            </Button>
          </div>
        </div>
      </Modal>
    </OperatingLayout>
  );
};
